import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { UserPlus, MapPin, Phone, Mail, User, ArrowLeft, CheckCircle, Camera, FileText, Users, Briefcase, CreditCard, ChevronRight, ChevronLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AgentRegisterEnhanced() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [agentCode, setAgentCode] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Basic Information
    full_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    address: '',
    city: '',
    state: '',
    regions_covered: [],
    
    // Step 2: Identity & Documents
    id_type: '',
    id_number: '',
    profile_photo: null,
    id_document: null,
    
    // Step 3: Qualifications
    education_level: '',
    certifications: [],
    qualification_docs: [],
    
    // Step 4: Professional Background
    has_law_enforcement_background: false,
    law_enforcement_details: '',
    years_of_experience: 0,
    previous_employers: [],
    
    // Step 5: Referees
    referees: [
      { name: '', phone: '', email: '', relationship: '' },
      { name: '', phone: '', email: '', relationship: '' }
    ],
    
    // Step 6: Payment Details
    bank_name: '',
    account_number: '',
    account_name: '',
    payment_method: 'bank_transfer',
    mobile_money_number: '',
    mobile_money_provider: ''
  });

  const [regionInput, setRegionInput] = useState('');
  const [certInput, setCertInput] = useState('');
  const [employerInput, setEmployerInput] = useState({ name: '', role: '', duration: '' });

  const totalSteps = 6;

  const handleAddRegion = () => {
    if (regionInput.trim() && !formData.regions_covered.includes(regionInput.trim())) {
      setFormData({
        ...formData,
        regions_covered: [...formData.regions_covered, regionInput.trim()]
      });
      setRegionInput('');
    }
  };

  const handleRemoveRegion = (region) => {
    setFormData({
      ...formData,
      regions_covered: formData.regions_covered.filter(r => r !== region)
    });
  };

  const handleAddCertification = () => {
    if (certInput.trim()) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, certInput.trim()]
      });
      setCertInput('');
    }
  };

  const handleRemoveCertification = (cert) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(c => c !== cert)
    });
  };

  const handleAddEmployer = () => {
    if (employerInput.name && employerInput.role) {
      setFormData({
        ...formData,
        previous_employers: [...formData.previous_employers, employerInput]
      });
      setEmployerInput({ name: '', role: '', duration: '' });
    }
  };

  const handleFileChange = (e, fieldName) => {
    const files = e.target.files;
    if (fieldName === 'qualification_docs') {
      setFormData({ ...formData, [fieldName]: Array.from(files) });
    } else {
      setFormData({ ...formData, [fieldName]: files[0] });
    }
  };

  const handleRefereeChange = (index, field, value) => {
    const newReferees = [...formData.referees];
    newReferees[index][field] = value;
    setFormData({ ...formData, referees: newReferees });
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        if (!formData.full_name || !formData.email || !formData.phone || formData.regions_covered.length === 0) {
          toast.error('Please fill all required fields in Basic Information');
          return false;
        }
        return true;
      case 2:
        if (!formData.id_type || !formData.id_number || !formData.profile_photo || !formData.id_document) {
          toast.error('Please provide identity documents and photo');
          return false;
        }
        return true;
      case 3:
        if (!formData.education_level || formData.qualification_docs.length === 0) {
          toast.error('Please provide education details and upload qualification documents');
          return false;
        }
        return true;
      case 5:
        const validReferees = formData.referees.filter(r => r.name && r.phone);
        if (validReferees.length < 2) {
          toast.error('Please provide at least 2 complete referees');
          return false;
        }
        return true;
      case 6:
        if (formData.payment_method === 'bank_transfer' && (!formData.bank_name || !formData.account_number || !formData.account_name)) {
          toast.error('Please provide complete bank details');
          return false;
        }
        if (formData.payment_method === 'mobile_money' && (!formData.mobile_money_number || !formData.mobile_money_provider)) {
          toast.error('Please provide complete mobile money details');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(Math.min(currentStep + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(currentStep)) return;

    setLoading(true);
    try {
      const submitData = new FormData();
      
      // Append text fields
      submitData.append('full_name', formData.full_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('regions_covered', JSON.stringify(formData.regions_covered));
      
      if (formData.date_of_birth) submitData.append('date_of_birth', formData.date_of_birth);
      if (formData.gender) submitData.append('gender', formData.gender);
      if (formData.address) submitData.append('address', formData.address);
      if (formData.city) submitData.append('city', formData.city);
      if (formData.state) submitData.append('state', formData.state);
      
      submitData.append('id_type', formData.id_type);
      submitData.append('id_number', formData.id_number);
      submitData.append('education_level', formData.education_level);
      submitData.append('certifications', JSON.stringify(formData.certifications));
      
      submitData.append('has_law_enforcement_background', formData.has_law_enforcement_background);
      if (formData.law_enforcement_details) submitData.append('law_enforcement_details', formData.law_enforcement_details);
      submitData.append('years_of_experience', formData.years_of_experience);
      submitData.append('previous_employers', JSON.stringify(formData.previous_employers));
      
      submitData.append('referees', JSON.stringify(formData.referees));
      
      submitData.append('payment_method', formData.payment_method);
      if (formData.bank_name) submitData.append('bank_name', formData.bank_name);
      if (formData.account_number) submitData.append('account_number', formData.account_number);
      if (formData.account_name) submitData.append('account_name', formData.account_name);
      if (formData.mobile_money_number) submitData.append('mobile_money_number', formData.mobile_money_number);
      if (formData.mobile_money_provider) submitData.append('mobile_money_provider', formData.mobile_money_provider);
      
      // Append files
      if (formData.profile_photo) submitData.append('profile_photo', formData.profile_photo);
      if (formData.id_document) submitData.append('id_document', formData.id_document);
      if (formData.qualification_docs.length > 0) {
        formData.qualification_docs.forEach((file) => {
          submitData.append('qualification_docs', file);
        });
      }

      const response = await axios.post(`${API}/agents/self-register`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAgentCode(response.data.agent_code);
      setRegistrationSuccess(true);
      toast.success('Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle size={48} className="text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Registration Successful!
            </h1>
            <p className="text-gray-600 mb-6">
              Your field agent registration has been submitted for review.
            </p>
            
            <div className="bg-slate-50 border-2 border-slate-200 rounded-xl p-6 mb-6">
              <p className="text-sm text-gray-600 mb-3">Your Agent Code:</p>
              <div className="text-4xl font-bold text-[#5a738f] mb-2 tracking-wider">
                {agentCode}
              </div>
              <p className="text-sm text-gray-500 mt-3">
                ⚠️ Save this code! You'll need it to login.
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-blue-900 mb-2">Next Steps:</h3>
              <ol className="text-sm text-blue-800 space-y-2 ml-4 list-decimal">
                <li>Save your agent code in a safe place</li>
                <li>Your application is under review by our admin team</li>
                <li>We'll verify your documents and referee information</li>
                <li>You'll receive notification once approved (usually within 48 hours)</li>
                <li>Once approved, login with your email and agent code</li>
              </ol>
            </div>

            <button
              onClick={() => navigate('/agent/login')}
              className="w-full bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Go to Agent Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        {[1, 2, 3, 4, 5, 6].map((step) => (
          <div key={step} className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${
              step <= currentStep ? 'bg-[#5a738f] text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step}
            </div>
            {step < 6 && (
              <div className={`flex-1 h-1 mx-2 ${
                step < currentStep ? 'bg-[#5a738f]' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 text-center">
        <p className="text-sm font-medium text-gray-600">
          Step {currentStep} of {totalSteps}: {getStepTitle(currentStep)}
        </p>
      </div>
    </div>
  );

  const getStepTitle = (step) => {
    const titles = {
      1: 'Basic Information',
      2: 'Identity & Documents',
      3: 'Qualifications',
      4: 'Professional Background',
      5: 'Referees',
      6: 'Payment Details'
    };
    return titles[step];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/hakicheck-logo.png" alt="HakiCheck" className="h-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Become a Field Agent
          </h1>
          <p className="text-gray-600">
            Complete the registration process to join our network
          </p>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <User size={24} className="text-[#5a738f]" />
                  Basic Information
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.full_name}
                      onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Gender
                    </label>
                    <select
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.gender}
                      onChange={(e) => setFormData({...formData, gender: e.target.value})}
                    >
                      <option value="">Select gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                      placeholder="Your city"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.state}
                      onChange={(e) => setFormData({...formData, state: e.target.value})}
                      placeholder="Your state"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address
                  </label>
                  <textarea
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="Your residential address"
                    rows={2}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin size={16} className="inline mr-2" />
                    Service Regions *
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={regionInput}
                      onChange={(e) => setRegionInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRegion())}
                      placeholder="Enter a city/state"
                    />
                    <button
                      type="button"
                      onClick={handleAddRegion}
                      className="px-6 py-3 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  
                  {formData.regions_covered.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.regions_covered.map((region, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-4 py-2 rounded-full text-sm bg-blue-100 text-blue-800 font-medium"
                        >
                          <MapPin size={14} className="mr-1" />
                          {region}
                          <button
                            type="button"
                            onClick={() => handleRemoveRegion(region)}
                            className="ml-2 text-blue-600 hover:text-blue-800 font-bold text-lg"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 2: Identity & Documents */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText size={24} className="text-[#5a738f]" />
                  Identity & Documents
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID Type *
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.id_type}
                      onChange={(e) => setFormData({...formData, id_type: e.target.value})}
                    >
                      <option value="">Select ID type</option>
                      <option value="national_id">National ID</option>
                      <option value="passport">Passport</option>
                      <option value="drivers_license">Driver's License</option>
                      <option value="voters_card">Voter's Card</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ID Number *
                    </label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.id_number}
                      onChange={(e) => setFormData({...formData, id_number: e.target.value})}
                      placeholder="Enter ID number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Camera size={16} className="inline mr-2" />
                    Profile Photo * (Your clear passport photo)
                  </label>
                  <input
                    type="file"
                    required
                    accept="image/*"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    onChange={(e) => handleFileChange(e, 'profile_photo')}
                  />
                  <p className="text-sm text-gray-500 mt-1">Upload a clear passport-style photo</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText size={16} className="inline mr-2" />
                    Identity Document * (Scan/Photo of your ID)
                  </label>
                  <input
                    type="file"
                    required
                    accept="image/*,application/pdf"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    onChange={(e) => handleFileChange(e, 'id_document')}
                  />
                  <p className="text-sm text-gray-500 mt-1">Upload clear scan or photo of your ID document</p>
                </div>
              </div>
            )}

            {/* Step 3: Qualifications */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText size={24} className="text-[#5a738f]" />
                  Education & Qualifications
                </h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Education Level *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    value={formData.education_level}
                    onChange={(e) => setFormData({...formData, education_level: e.target.value})}
                  >
                    <option value="">Select education level</option>
                    <option value="secondary">Secondary School</option>
                    <option value="ond">OND</option>
                    <option value="hnd">HND</option>
                    <option value="bachelors">Bachelor's Degree</option>
                    <option value="masters">Master's Degree</option>
                    <option value="phd">PhD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certifications (Optional)
                  </label>
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={certInput}
                      onChange={(e) => setCertInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCertification())}
                      placeholder="e.g., Private Investigator License"
                    />
                    <button
                      type="button"
                      onClick={handleAddCertification}
                      className="px-6 py-3 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-medium rounded-lg transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  {formData.certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.certifications.map((cert, idx) => (
                        <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                          {cert}
                          <button
                            type="button"
                            onClick={() => handleRemoveCertification(cert)}
                            className="ml-2 text-green-600 hover:text-green-800 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qualification Documents * (Certificates, transcripts, etc.)
                  </label>
                  <input
                    type="file"
                    required
                    multiple
                    accept="image/*,application/pdf"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    onChange={(e) => handleFileChange(e, 'qualification_docs')}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Upload your educational certificates and qualifications (multiple files allowed)
                  </p>
                  {formData.qualification_docs.length > 0 && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {formData.qualification_docs.length} file(s) selected
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Professional Background */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase size={24} className="text-[#5a738f]" />
                  Professional Background
                </h2>

                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      className="w-5 h-5 text-[#5a738f] border-gray-300 rounded focus:ring-[#5a738f]"
                      checked={formData.has_law_enforcement_background}
                      onChange={(e) => setFormData({...formData, has_law_enforcement_background: e.target.checked})}
                    />
                    <span className="text-sm font-medium text-gray-700">
                      I have law enforcement or security background
                    </span>
                  </label>
                </div>

                {formData.has_law_enforcement_background && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Law Enforcement Details
                    </label>
                    <textarea
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                      value={formData.law_enforcement_details}
                      onChange={(e) => setFormData({...formData, law_enforcement_details: e.target.value})}
                      placeholder="Describe your law enforcement or security experience..."
                      rows={4}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Relevant Experience
                  </label>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    value={formData.years_of_experience}
                    onChange={(e) => setFormData({...formData, years_of_experience: parseInt(e.target.value) || 0})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Previous Employers (Optional)
                  </label>
                  <div className="border border-gray-300 rounded-lg p-4 mb-3">
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Company name"
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#5a738f]"
                        value={employerInput.name}
                        onChange={(e) => setEmployerInput({...employerInput, name: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Role"
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#5a738f]"
                        value={employerInput.role}
                        onChange={(e) => setEmployerInput({...employerInput, role: e.target.value})}
                      />
                      <input
                        type="text"
                        placeholder="Duration (e.g., 2 years)"
                        className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-[#5a738f]"
                        value={employerInput.duration}
                        onChange={(e) => setEmployerInput({...employerInput, duration: e.target.value})}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddEmployer}
                      className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded transition-colors"
                    >
                      Add Employer
                    </button>
                  </div>
                  {formData.previous_employers.length > 0 && (
                    <div className="space-y-2">
                      {formData.previous_employers.map((emp, idx) => (
                        <div key={idx} className="bg-gray-50 p-3 rounded-lg">
                          <p className="font-medium">{emp.name} - {emp.role}</p>
                          <p className="text-sm text-gray-600">{emp.duration}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 5: Referees */}
            {currentStep === 5 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Users size={24} className="text-[#5a738f]" />
                  Referees (Minimum 2 Required)
                </h2>
                <p className="text-gray-600 mb-4">
                  Provide at least 2 referees who can vouch for your character and work ethic
                </p>

                {formData.referees.map((referee, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Referee {index + 1}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                          value={referee.name}
                          onChange={(e) => handleRefereeChange(index, 'name', e.target.value)}
                          placeholder="Referee's full name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                          value={referee.phone}
                          onChange={(e) => handleRefereeChange(index, 'phone', e.target.value)}
                          placeholder="+234 XXX XXX XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                          value={referee.email}
                          onChange={(e) => handleRefereeChange(index, 'email', e.target.value)}
                          placeholder="referee@example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Relationship
                        </label>
                        <input
                          type="text"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                          value={referee.relationship}
                          onChange={(e) => handleRefereeChange(index, 'relationship', e.target.value)}
                          placeholder="e.g., Former Supervisor, Colleague"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={() => setFormData({
                    ...formData,
                    referees: [...formData.referees, { name: '', phone: '', email: '', relationship: '' }]
                  })}
                  className="w-full px-4 py-3 border-2 border-dashed border-gray-300 hover:border-[#5a738f] text-gray-600 hover:text-[#5a738f] font-medium rounded-lg transition-colors"
                >
                  + Add Another Referee
                </button>
              </div>
            )}

            {/* Step 6: Payment Details */}
            {currentStep === 6 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard size={24} className="text-[#5a738f]" />
                  Payment Details for Remuneration
                </h2>
                <p className="text-gray-600 mb-4">
                  Provide your payment details to receive compensation for completed jobs
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                    value={formData.payment_method}
                    onChange={(e) => setFormData({...formData, payment_method: e.target.value})}
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                  </select>
                </div>

                {formData.payment_method === 'bank_transfer' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bank Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                        value={formData.bank_name}
                        onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
                        placeholder="e.g., First Bank"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Number *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                        value={formData.account_number}
                        onChange={(e) => setFormData({...formData, account_number: e.target.value})}
                        placeholder="0123456789"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Account Name *
                      </label>
                      <input
                        type="text"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                        value={formData.account_name}
                        onChange={(e) => setFormData({...formData, account_name: e.target.value})}
                        placeholder="Account holder name"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile Money Provider *
                      </label>
                      <select
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                        value={formData.mobile_money_provider}
                        onChange={(e) => setFormData({...formData, mobile_money_provider: e.target.value})}
                      >
                        <option value="">Select provider</option>
                        <option value="mtn_momo">MTN Mobile Money</option>
                        <option value="airtel_money">Airtel Money</option>
                        <option value="glo_money">Glo Money</option>
                        <option value="paga">Paga</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile Money Number *
                      </label>
                      <input
                        type="tel"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#5a738f] focus:border-transparent"
                        value={formData.mobile_money_number}
                        onChange={(e) => setFormData({...formData, mobile_money_number: e.target.value})}
                        placeholder="+234 XXX XXX XXXX"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <ChevronLeft size={20} />
                  Previous
                </button>
              )}
              
              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#5a738f] hover:bg-[#4a5f7f] text-white font-semibold rounded-lg transition-colors"
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit Registration'}
                </button>
              )}
            </div>
          </form>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200 text-center space-y-2">
            <button
              onClick={() => navigate('/agent/login')}
              className="text-[#5a738f] hover:text-[#4a5f7f] font-medium"
            >
              Already have an agent code? Login here
            </button>
            <br />
            <button
              onClick={() => navigate('/')}
              className="text-gray-600 hover:text-gray-800 text-sm inline-flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgentRegisterEnhanced;
